import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'
import { MAIN_COMPANY_SCOPE_VALUE } from './monthlyConfigurationScope.js'

const companyInfo = ref(null)
const branches = ref([])
const isLoadingBranches = ref(false)

export const ALL_BRANCHES_SCOPE_VALUE = 'all'

function branchIsActive(row) {
  if (!row || typeof row !== 'object') return false
  const status = row.is_active ?? row.isActive
  return status === 1 || status === '1' || status === true
}

export function useMonthlyConfigurationBranches() {
  const store = useStore()

  const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

  const companyId = computed(() =>
    currentUser.value?.companyId ||
    currentUser.value?.company_id ||
    currentUser.value?.company?.id ||
    companyInfo.value?.id ||
    null
  )

  const companyName = computed(() => {
    const fromApi = companyInfo.value?.name
    if (fromApi) return String(fromApi)
    return (
      currentUser.value?.company?.name ||
      currentUser.value?.companyName ||
      currentUser.value?.company_name ||
      'Company'
    )
  })

  const branchSelectOptions = computed(() => {
    const name = companyName.value
    const options = [
      {
        value: ALL_BRANCHES_SCOPE_VALUE,
        label: 'All'
      },
      {
        value: MAIN_COMPANY_SCOPE_VALUE,
        label: `${name} - Main company`
      }
    ]
    for (const branch of branches.value) {
      const branchName = branch.name || 'Branch'
      options.push({
        value: String(branch.id),
        label: `${branchName}`
      })
    }
    return options
  })

  async function loadBranches() {
    if (!companyId.value && !currentUser.value) {
      companyInfo.value = null
      branches.value = []
      return
    }

    isLoadingBranches.value = true
    try {
      const response = await apiFetch('/branches/monthly-configuration/active', { method: 'GET' })
      if (!response.ok) {
        companyInfo.value = null
        branches.value = []
        return
      }

      const data = await response.json().catch(() => ({}))
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        companyInfo.value = data.company && typeof data.company === 'object' ? data.company : null
        const rows = Array.isArray(data.branches) ? data.branches : []
        branches.value = rows.filter(branchIsActive)
        return
      }

      // Backward compatibility if API returns a plain branch array
      const rows = Array.isArray(data) ? data : []
      companyInfo.value = companyId.value
        ? { id: companyId.value, name: companyName.value }
        : null
      branches.value = rows.filter(branchIsActive)
    } catch (err) {
      console.error('Error loading branches for monthly configuration:', err)
      companyInfo.value = null
      branches.value = []
    } finally {
      isLoadingBranches.value = false
    }
  }

  return {
    branches,
    branchSelectOptions,
    companyInfo,
    companyName,
    isLoadingBranches,
    loadBranches,
    companyId,
    ALL_BRANCHES_SCOPE_VALUE
  }
}
